import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import GufateTicker from "@/components/GufateTicker";
import PitchTacticalBackground from "@/components/PitchTacticalBackground";
import CountdownHero from "@/components/CountdownHero";
import WeatherMatch from "@/components/WeatherMatch";
import InterviewsSection from "@/components/InterviewsSection";
import WeeklyMvpCard from "@/components/WeeklyMvpCard";
import { MOOD_PRESETS, autoPresetFromTime, loadStoredPreset, storePreset } from "@/lib/moodPresets";
import { unlockAudio } from "@/lib/stadiumSound";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { GufoIcon } from "@/components/GufoIcon";
import { api, publicFileUrl } from "@/lib/api";
import { Mic, Ghost, Users, Dice5, Trophy, Sparkles, Coins, Flame, Award, Info } from "lucide-react";
import GironeBadge from "@/components/GironeBadge";
import useStandalone from "@/hooks/useStandalone";

const SHIELD_PLACEHOLDER = "https://images.unsplash.com/photo-1577214322326-5b31d9d886b0?crop=entropy&cs=srgb&fm=jpg&w=300&q=80";
const ROLE_COLORS = { PT: "#f59e0b", DEF: "#0ea5e9", CEN: "#3b56e0", ATT: "#f97316" };
const ROLE_LABEL = { PT: "Portieri", DEF: "Difensori", CEN: "Centrocampisti", ATT: "Attaccanti" };

export default function Home() {
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState({ top_priced: null, best_perf: null, latest_results: [] });
  const [latestMd, setLatestMd] = useState(null);
  const [mood, setMood] = useState(() => loadStoredPreset());
  const isStandalone = useStandalone();

  useEffect(() => {
    api.get("/public/teams").then(({ data }) => setTeams(data)).catch(() => {});
    api.get("/public/home-stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/public/mvp-weekly").then(({ data }) => setLatestMd(data?.matchday)).catch(() => {});
  }, []);

  // Refresh auto preset every 5 minutes so it follows the clock while the tab is open.
  useEffect(() => {
    if (!mood.auto) return;
    const t = setInterval(() => {
      const next = autoPresetFromTime();
      setMood((m) => (m.auto && m.id !== next ? { ...m, id: next } : m));
    }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [mood.auto]);

  const handleMoodChange = ({ id, auto }) => {
    if (auto) {
      const next = { id: autoPresetFromTime(), auto: true };
      setMood(next);
      storePreset(next.id, true);
    } else {
      setMood({ id, auto: false });
      storePreset(id, false);
    }
  };

  const activePreset = MOOD_PRESETS[mood.id] || MOOD_PRESETS.notte;

  const byGirone = { A: [], B: [], C: [], D: [] };
  teams.forEach((t) => { if (byGirone[t.girone]) byGirone[t.girone].push(t); });

  return (
    <div className="App">
      <Header />

      {/* HERO with pitch + tactical background */}
      <section className="relative overflow-hidden">
        {/* Pitch + stadium base */}
        <PitchTacticalBackground preset={activePreset.id} />
        {/* Dynamic veil per preset — keeps text readable while tinting the mood */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: activePreset.veil }} />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-8 md:pt-10">
          {/* Top cruscotto: Countdown a sinistra, Meteo a destra */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CountdownHero />
            <WeatherMatch />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="fade-up relative">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/95 border border-slate-200 text-xs uppercase tracking-widest text-slate-700 shadow-sm">
                <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--orange)" }} /> Stagione 2026/27
              </div>
              <h1 className="display font-black text-[2.4rem] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] mt-5 text-slate-900" style={{ textShadow: "0 1px 0 rgba(255,255,255,0.6)" }}>
                Fantacampionato
                <br />
                <span style={{ color: "var(--brand)" }}>FIFE</span>{" "}
                <span style={{ color: "var(--orange)" }}>2026</span>
              </h1>
              <p className="text-slate-800 text-base md:text-lg mt-5 max-w-xl font-medium">
                La <b>Fantalega Élite Italia</b> è il Torneo Fantacalcistico Nazionale più innovativo di sempre! Campionati, Coppe, Fantascommesse e… Gufate! Il meglio del Fantacalcio, tra leghe private e competizione su larga scala.
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                {isStandalone ? (
                  <Link to="/login">
                    <Button onClick={unlockAudio} className="btn-brand rounded-full px-8 h-12 text-base font-semibold shadow-xl" data-testid="hero-cta-login">Accedi</Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/register">
                      <Button onClick={unlockAudio} className="btn-brand rounded-full px-7 h-12 text-base font-semibold shadow-xl" data-testid="hero-cta-register">Iscriviti al torneo</Button>
                    </Link>
                    <Link to="/login">
                      <Button onClick={unlockAudio} variant="outline" className="rounded-full bg-white/95 border-slate-200 text-slate-900 hover:bg-white h-12 px-7 shadow-lg" data-testid="hero-cta-login">Ho già un account</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="relative flex justify-center lg:justify-end">
            <img src="/logo-fife.png" alt="FIFE" className="w-full max-w-[420px] md:max-w-[520px] lg:max-w-[600px] h-auto drop-shadow-[0_20px_40px_rgba(0,0,0,0.35)] scale-in float" style={{ animationDelay: "180ms" }} />
          </div>
        </div>
      </section>

      {/* Ultime gufate (breaking-news dark bar) */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8">
        <GufateTicker />
      </section>

      {/* Interviste dalla sala stampa FIFE */}
      <InterviewsSection />

      {/* MVP della Settimana */}
      <WeeklyMvpCard />

      {/* Best Performer di Giornata (per fantapunteggio) */}
      {stats.top_scorer && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
          <SectionHeading icon={Trophy} color="#eab308"
                          eyebrow={`Miglior fantacalciatore · Giornata ${stats.top_scorer.matchday ?? ""}`}
                          title="Best Performer" />
          <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden"
               style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fef08a 50%, #fde047 100%)" }}
               data-testid="home-top-scorer">
            <div className="absolute -top-10 -right-10 text-[200px] leading-none opacity-10">⚽</div>
            <div className="relative flex flex-wrap items-center gap-6">
              <div className="w-20 h-20 rounded-2xl bg-white/70 backdrop-blur flex items-center justify-center text-5xl shadow-lg">🥇</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-yellow-900 font-bold">
                  {stats.top_scorer.role}
                </div>
                <div className="display font-black text-3xl md:text-4xl text-yellow-950 leading-tight">
                  {stats.top_scorer.name}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="top-performer-teams">
                  {(stats.top_scorer.teams || []).map((t, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/80 text-yellow-900 text-[11px] font-bold">
                      {t.girone && <GironeBadge girone={t.girone} size={14} />}
                      {t.team_name}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-center bg-white/60 rounded-2xl px-6 py-3 backdrop-blur">
                <div className="display font-black text-5xl md:text-6xl tabular-nums text-yellow-950" data-testid="top-performer-fp">
                  {Number(stats.top_scorer.fantavoto ?? 0).toFixed(1)}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-yellow-900 font-bold">Fantapunti</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Best Performance */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <SectionHeading icon={Flame} color="var(--orange)" eyebrow={latestMd ? `Giornata ${latestMd}` : "Regular season"} title="Best Performance" />
          <Link to="/performance"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-black uppercase tracking-widest text-slate-900 border border-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
                data-testid="best-perf-see-all">
            Vai alle Performance →
          </Link>
        </div>
        <RoleAccordion
          data={stats.best_perf}
          renderValue={(p) => <span className="display font-black" style={{ color: ROLE_COLORS[p.role] }}>{p.rating?.toFixed(1)}</span>}
          valueLabel="Voto"
          testid="best-perf"
        />
      </section>

      {/* Ultimi risultati — 4 card per girone */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <SectionHeading icon={Award} color="var(--cyan)" eyebrow="Regular season" title="Ultimi risultati" />
        <ResultsByGironeCards results={stats.latest_results || []} />
      </section>

      {/* I Giocatori più pagati */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <SectionHeading icon={Coins} color="var(--brand)" eyebrow="Aste 2026/27" title="I Giocatori più pagati" />
          <Link to="/aste"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-black uppercase tracking-widest text-slate-900 border border-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
                data-testid="top-priced-see-all">
            Vai alle aste →
          </Link>
        </div>
        <RoleAccordion
          data={stats.top_priced}
          renderValue={(p) => <span className="display font-black" style={{ color: "var(--brand)" }}>{p.credits}</span>}
          valueLabel="Crediti"
          testid="top-priced"
        />
      </section>

      {/* Squadre & gironi */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <SectionHeading icon={Trophy} color="var(--brand)" title="Squadre & Gironi" />
        <Tabs defaultValue="A" className="w-full mt-4">
          <TabsList className="bg-slate-100 border border-slate-200 w-full sm:w-auto grid grid-cols-4 sm:inline-flex">
            {["A", "B", "C", "D"].map((g) => (
              <TabsTrigger key={g} value={g} className="data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white inline-flex items-center gap-1.5 px-2 sm:px-3" data-testid={`girone-tab-${g}`}>
                <GironeBadge girone={g} size={18} />
                <span className="hidden sm:inline">Girone</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {["A", "B", "C", "D"].map((g) => (
            <TabsContent key={g} value={g} className="mt-6">
              {byGirone[g].length === 0 ? (
                <Empty text={`Nessuna squadra approvata nel Girone ${g}.`} testid={`girone-empty-${g}`} />
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger">
                  {byGirone[g].map((t, i) => (
                    <Link key={t.user_id} to={`/squadra/${t.user_id}`} className="card-surface rounded-2xl p-3 card-lift block group" data-testid={`public-team-${g}-${i}`}>
                      <div className="aspect-square bg-slate-50 rounded-xl overflow-hidden mb-3">
                        <img src={t.shield_path ? publicFileUrl(t.shield_path) : SHIELD_PLACEHOLDER} alt={t.team_name}
                             className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                             onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }} />
                      </div>
                      <div className="display font-bold text-sm truncate">{t.team_name}</div>
                      <div className="text-slate-500 text-xs truncate">{t.coach_name}</div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* Armi del fantallenatore */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <SectionHeading icon={Sparkles} color="var(--brand)" title="Le armi del fantallenatore" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 stagger">
          {[
            { icon: Mic, label: "Conferenza stampa", color: "var(--conferenza)", desc: "Dichiara al mondo la tua strategia prima di ogni giornata." },
            { icon: Ghost, label: "Gufata", color: "var(--gufata)", desc: "Porta sfortuna al rivale con una maledizione a tempo." },
            { icon: Users, label: "Cerca supporter", color: "var(--supporter)", desc: "Trova alleati che ti regalano Gufi in cambio di visibilità." },
            { icon: Dice5, label: "Fantascommetti", color: "var(--scommetti)", desc: "Punta i tuoi Gufi sui pronostici della giornata." },
          ].map((f, i) => (
            <div key={i} className="card-surface rounded-2xl p-6 card-lift group">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" style={{ background: `${f.color}22`, color: f.color }}>
                <f.icon className="w-5 h-5" />
              </div>
              <div className="display font-bold text-lg">{f.label}</div>
              <p className="text-slate-600 text-sm mt-2">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Chi Siamo */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="chi-siamo">
        <SectionHeading icon={Info} color="var(--brand)" eyebrow="About" title="Chi Siamo" />
        <div className="card-surface rounded-3xl p-6 md:p-10 mt-4 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-center">
          <img src="/logo-fife.png" alt="FIFE" className="w-56 md:w-80 lg:w-96 h-auto shrink-0 mx-auto md:mx-0" />
          <div className="space-y-4 text-slate-700 text-[15px] md:text-base leading-relaxed">
            <p>
              La FIFE è un'Associazione senza scopo di lucro nata a Udine nel 2023 in virtù della
              comune volontà di un gruppo di amici di riuscire a promuovere e consolidare il Fantacalcio
              a livello nazionale, in particolare a mezzo della creazione di una competizione che
              riuscisse a coniugare tutti i benefici associati alla tradizionale formula delle Leghe
              Private ad 8 squadre con l'intrattenimento e gli obiettivi sfidanti di una competizione
              più ampiamente diffusa.
            </p>
            <p>
              La FIFE articola dunque la propria competizione attorno a un impianto consolidato in
              termini di regolamento e logiche di calcolo, impianto che viene poi declinato sia nel
              Campionato Generale che nella Coppa in pieno stile Champions League (dunque gironi di
              qualificazione e fase ad eliminazione diretta).
            </p>
            <p>
              L'idea è dunque quella di riuscire ad essere il volano per la diffusione di un nuovo
              modo di intendere il Fantacalcio attraverso un metodo scalabile che consenta agli amici
              di sempre di competere contestualmente sia tra di loro che contro tutti gli altri.
            </p>
          </div>
        </div>
      </section>

      <Footer showNewsletter />
    </div>
  );
}

function ResultsByGirone({ results }) {
  const by = { A: [], B: [], C: [], D: [] };
  results.forEach((r) => { if (by[r.girone]) by[r.girone].push(r); });
  return (
    <Tabs defaultValue="A" className="w-full mt-4">
      <TabsList className="bg-slate-100 border border-slate-200 w-full sm:w-auto grid grid-cols-4 sm:inline-flex">
        {["A", "B", "C", "D"].map((g) => (
          <TabsTrigger key={g} value={g} className="data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white px-2 sm:px-3" data-testid={`results-tab-${g}`}>
            <span className="sm:hidden">{g}</span>
            <span className="hidden sm:inline">Girone {g}</span>
          </TabsTrigger>
        ))}
      </TabsList>
      {["A", "B", "C", "D"].map((g) => (
        <TabsContent key={g} value={g} className="mt-6">
          {by[g].length === 0 ? (
            <div className="card-surface rounded-2xl p-8 text-center text-slate-500">Nessuna partita nel Girone {g}.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {by[g].map((r, i) => <ResultRow key={i} r={r} />)}
            </div>
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
}

function SectionHeading({ icon: Icon, color, eyebrow, title }) {
  return (
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        {eyebrow && <div className="text-[10px] uppercase tracking-widest text-slate-500">{eyebrow}</div>}
        <h2 className="display font-black text-2xl md:text-3xl tracking-tight">{title}</h2>
      </div>
    </div>
  );
}

function RoleAccordion({ data, renderValue, valueLabel, testid }) {
  if (!data) return <div className="text-slate-500 text-sm mt-4">Caricamento statistiche…</div>;
  return (
    <Accordion type="multiple" defaultValue={["PT"]} className="mt-4 card-surface rounded-2xl px-4">
      {["PT", "DEF", "CEN", "ATT"].map((role) => {
        const rows = data[role] || [];
        return (
          <AccordionItem key={role} value={role} className="border-slate-200">
            <AccordionTrigger data-testid={`${testid}-trigger-${role}`} className="hover:no-underline">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-black px-2 py-0.5 rounded" style={{ background: `${ROLE_COLORS[role]}22`, color: ROLE_COLORS[role] }}>{role}</span>
                <span className="display font-bold text-base">{ROLE_LABEL[role]}</span>
                <span className="text-slate-500 text-xs">Top 5</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <table className="w-full text-sm mt-1">
                <thead className="text-slate-500 uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="text-left py-2 w-8">#</th>
                    <th className="text-left py-2">Giocatore</th>
                    <th className="text-left py-2">Squadre</th>
                    <th className="text-right py-2">{valueLabel}</th>
                  </tr>
                </thead>
                <tbody>
                    {rows.map((p, i) => (
                    <tr key={p.name} className="border-t border-slate-100" data-testid={`${testid}-${role}-${i}`}>
                      <td className="py-2 text-slate-500 align-top">{i + 1}</td>
                      <td className="py-2 text-slate-900 align-top font-semibold">{p.name}</td>
                      <td className="py-2 text-slate-500 align-top">
                        {p.teams?.length ? (
                          <div className="flex flex-wrap gap-1" data-testid={`${testid}-teams-${role}-${i}`}>
                            {p.teams.map((tm, j) => {
                              const nm = typeof tm === "string" ? tm : tm.team_name;
                              const gr = typeof tm === "string" ? null : tm.girone;
                              return (
                                <span key={`${nm}-${j}`}
                                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1 sm:px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                                      title={nm}>
                                  {gr && <GironeBadge girone={gr} size={14} />}
                                  <span className="hidden sm:inline truncate max-w-[140px]">{nm}</span>
                                </span>
                              );
                            })}
                          </div>
                        ) : (p.team || "-")}
                      </td>
                      <td className="py-2 text-right align-top">{renderValue(p)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

function ResultRow({ r }) {
  return (
    <div className="card-surface rounded-2xl p-4 flex items-center gap-3" data-testid={`result-${r.home?.user_id}-${r.away?.user_id}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0">G{r.matchday} · {r.girone}</div>
      <div className="flex-1 flex items-center gap-3 min-w-0">
        <img src={r.home?.shield_path ? publicFileUrl(r.home.shield_path) : SHIELD_PLACEHOLDER} onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }} alt="" className="w-8 h-8 rounded-md object-cover" />
        <Link to={`/squadra/${r.home?.user_id}`} className="text-sm truncate hover:text-slate-900">{r.home?.team_name}</Link>
      </div>
      <div className="display font-black text-lg tracking-tight text-slate-900 shrink-0">
        {r.home_score} <span className="text-slate-400">–</span> {r.away_score}
      </div>
      <div className="flex-1 flex items-center gap-3 justify-end min-w-0">
        <Link to={`/squadra/${r.away?.user_id}`} className="text-sm truncate text-right hover:text-slate-900">{r.away?.team_name}</Link>
        <img src={r.away?.shield_path ? publicFileUrl(r.away.shield_path) : SHIELD_PLACEHOLDER} onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }} alt="" className="w-8 h-8 rounded-md object-cover" />
      </div>
    </div>
  );
}

function ResultsByGironeCards({ results }) {
  const by = { A: [], B: [], C: [], D: [] };
  (results || []).forEach((r) => { if (by[r.girone]) by[r.girone].push(r); });
  const gironeAccent = { A: "#3b56e0", B: "#f97316", C: "#06b6d4", D: "#a855f7" };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="results-by-girone">
      {["A", "B", "C", "D"].map((g) => {
        const rows = by[g];
        const accent = gironeAccent[g];
        return (
          <div key={g} className="card-surface rounded-2xl overflow-hidden fade-up" data-testid={`results-card-${g}`}>
            <div className="flex items-center justify-between px-5 py-3" style={{ background: `${accent}12`, borderBottom: `1px solid ${accent}33` }}>
              <div className="flex items-center gap-3">
                <GironeBadge girone={g} size={40} />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Girone</div>
                  <div className="display font-black text-lg leading-tight">Girone {g}</div>
                </div>
              </div>
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{rows.length} {rows.length === 1 ? "partita" : "partite"}</span>
            </div>
            <div className="divide-y divide-slate-100">
              {rows.length === 0 ? (
                <div className="px-5 py-6 text-slate-400 text-sm italic text-center">Ancora nessuna partita disputata.</div>
              ) : (
                rows.map((r, i) => <CompactResultRow key={i} r={r} />)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CompactResultRow({ r }) {
  const homeWin = (r.home_score || 0) > (r.away_score || 0);
  const awayWin = (r.away_score || 0) > (r.home_score || 0);
  return (
    <div className="px-3 sm:px-5 py-3" data-testid={`result-${r.home?.user_id}-${r.away?.user_id}`}>
      {/* Mobile: layout stacked su 2 righe. Desktop: layout inline classico. */}
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 shrink-0 w-6 sm:w-8">G{r.matchday}</div>
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <img src={r.home?.shield_path ? publicFileUrl(r.home.shield_path) : SHIELD_PLACEHOLDER}
               onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }}
               alt="" className="w-7 h-7 rounded-md object-cover shrink-0" />
          <Link to={`/squadra/${r.home?.user_id}`}
                className={`text-[13px] sm:text-sm truncate hover:text-slate-900 ${homeWin ? "font-black text-slate-900" : "text-slate-600"}`}
                title={r.home?.team_name}>
            {r.home?.team_name}
          </Link>
        </div>
        <div className="display font-black text-base tracking-tight shrink-0 tabular-nums px-1">
          <span className={homeWin ? "text-slate-900" : "text-slate-400"}>{r.home_score}</span>
          <span className="text-slate-300 mx-1">–</span>
          <span className={awayWin ? "text-slate-900" : "text-slate-400"}>{r.away_score}</span>
        </div>
        <div className="flex-1 flex items-center gap-2 justify-end min-w-0">
          <Link to={`/squadra/${r.away?.user_id}`}
                className={`text-[13px] sm:text-sm truncate text-right hover:text-slate-900 ${awayWin ? "font-black text-slate-900" : "text-slate-600"}`}
                title={r.away?.team_name}>
            {r.away?.team_name}
          </Link>
          <img src={r.away?.shield_path ? publicFileUrl(r.away.shield_path) : SHIELD_PLACEHOLDER}
               onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }}
               alt="" className="w-7 h-7 rounded-md object-cover shrink-0" />
        </div>
      </div>
    </div>
  );
}

function Empty({ text, testid }) {
  return <div className="card-surface rounded-2xl p-8 text-center text-slate-500" data-testid={testid}>{text}</div>;
}
